/**
 * Chi sta guardando la partita in corso — la lista che vedono i due GIOCATORI.
 *
 * Separato da `spectator-store` (che è il contrario: lo stato di chi guarda),
 * e separato dai tre store di partita perché il dato è lo stesso in partita
 * online, in arena e in torneo: `game:spectators` arriva su un socket solo e
 * viene registrato una volta sola, in multiplayer-store.
 *
 * Non ha nessun aggancio al ciclo di vita della partita, e non gli serve: la
 * lettura passa da `useGameSpectators`, che confronta la room dell'evento con
 * quella della chat — l'unico posto dove tutte e tre le modalità scrivono qual è
 * la partita a schermo. Un evento in ritardo non può quindi colorare il badge
 * della damiera successiva, che in arena arriva pochi secondi dopo.
 */

import { create } from 'zustand';
import type { GameSpectator, GameSpectatorsEvent } from '@draughts/shared';
import { useChatStore } from './chat-store';

interface SpectatorsStore {
  /** La room a cui si riferisce la lista, non quella a schermo. */
  roomId: string | null;
  count: number;
  spectators: GameSpectator[];
  receive: (event: GameSpectatorsEvent) => void;
  /** Seme dei test: azzera fra un caso e l'altro. */
  reset: () => void;
}

/** Firma di un evento: cambia solo quando cambia qualcosa che si vede. */
function firma(roomId: string | null, spectators: GameSpectator[]): string {
  return `${roomId ?? ''}|${spectators.map((s) => s.userId).join(',')}`;
}

export const useSpectatorsStore = create<SpectatorsStore>((set, get) => ({
  roomId: null,
  count: 0,
  spectators: [],

  /**
   * L'evento è autorevole: sostituisce, non somma.
   *
   * Il `return` a mani vuote non è un'ottimizzazione da manuale, è ciò che rende
   * sostenibile il battito del server: `game:spectators` arriva ogni secondo
   * finché qualcuno guarda, sempre uguale, e senza questa guardia la barra di
   * gioco si ri-renderizzerebbe una volta al secondo per tutta la partita.
   */
  receive: (event) => {
    const { roomId, spectators } = get();
    if (firma(roomId, spectators) === firma(event.roomId, event.spectators)) return;
    set({ roomId: event.roomId, count: event.count, spectators: event.spectators });
  },

  reset: () => { set({ roomId: null, count: 0, spectators: [] }); },
}));

/** Riferimento stabile: un oggetto nuovo a ogni render basterebbe a far ri-renderizzare. */
const VUOTO: { count: number; spectators: GameSpectator[] } = { count: 0, spectators: [] };

/**
 * Gli spettatori della partita A SCHERMO — zero se l'ultimo evento parlava di
 * un'altra room.
 *
 * Selettori separati e primitivi di proposito: `useShallow` non si usa in questo
 * progetto, e un selettore che costruisse un oggetto ne restituirebbe uno nuovo
 * a ogni render, ri-renderizzando a ogni minimo cambio di store.
 */
export function useGameSpectators(): { count: number; spectators: GameSpectator[] } {
  const roomInCorso = useChatStore((s) => s.roomId);
  const roomEvento = useSpectatorsStore((s) => s.roomId);
  const count = useSpectatorsStore((s) => s.count);
  const spectators = useSpectatorsStore((s) => s.spectators);

  if (!roomInCorso || roomEvento !== roomInCorso) return VUOTO;
  return { count, spectators };
}
